import './ConstraintSaveBar.scss';
import { PropsWithChildren, useCallback, useRef } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router-dom';
import { MdSave } from 'react-icons/md';
import { AlertDialog } from '../AlertDialog';
import { Badge } from '../Badge';
import { Button } from '../Button';
import { IconButton } from '../IconButton';
import { Appearance, Intent, Size } from '../designConstants';

interface ConstraintSaveBarProps extends PropsWithChildren {
  changeCount: number;
  saving: boolean;
  disabled?: boolean;
  validating?: boolean;
  validationError?: string;
  onSave: () => void;
  onDiscard: () => void;
  onRetryValidation?: () => void;
}

export const ConstraintSaveBar = ({
  children,
  changeCount,
  saving,
  disabled = false,
  validating = false,
  validationError,
  onSave,
  onDiscard,
  onRetryValidation
}: ConstraintSaveBarProps) => {
  const dirty = changeCount > 0;
  const leaving = useRef(false);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search)
  );
  useBeforeUnload(
    useCallback(
      (event: BeforeUnloadEvent) => {
        if (dirty) {
          event.preventDefault();
        }
      },
      [dirty]
    )
  );
  let saveHint = disabled && dirty ? 'Resolve constraint problems before saving' : 'Save changes';
  if (validating) {
    saveHint = 'Checking constraints…';
  }
  if (validationError) {
    saveHint = 'Retry validation before saving';
  }
  if (saving) {
    saveHint = 'Saving…';
  }

  return (
    <div className='constraint-save-bar' data-testid='constraint-save-bar'>
      {children}
      <div className='constraint-save-actions'>
        {validating && !saving && <span>Checking constraints…</span>}
        {validationError && (
          <>
            <span role='alert'>Couldn’t check constraints.</span>
            <Button appearance={Appearance.OUTLINED} onClick={onRetryValidation}>
              Retry validation
            </Button>
          </>
        )}
        <Button
          size={Size.LG}
          appearance={Appearance.OUTLINED}
          disabled={!dirty || saving}
          onClick={onDiscard}
        >
          Discard
        </Button>
        <span className='constraint-save-button' title={saveHint}>
          <IconButton
            aria-label={`Save changes (${changeCount} unsaved)`}
            data-testid='save-constraints'
            disabled={!dirty || disabled || validating || Boolean(validationError)}
            loading={saving}
            onClick={onSave}
          >
            <MdSave />
          </IconButton>
          {dirty && (
            <Badge size={Size.SM} className='constraint-change-count'>
              {changeCount}
            </Badge>
          )}
        </span>
      </div>
      <AlertDialog
        show={blocker.state === 'blocked'}
        onHide={() => {
          if (!leaving.current) {
            blocker.reset?.();
          }
          leaving.current = false;
        }}
        intent={Intent.WARNING}
        title='Discard unsaved changes?'
        body='Your constraint edits have not been saved.'
        loading={saving}
        primaryAction={{
          onClick: () => {
            leaving.current = true;
            blocker.proceed?.();
          },
          label: 'Discard and leave'
        }}
        secondaryAction={{ onClick: () => blocker.reset?.(), label: 'Keep editing' }}
      />
    </div>
  );
};
